import firebase from "./firebase.config";

export const getWorkerImg = (workerId: string): Promise<string> => {
  const storage = firebase.storage();
  const storageRef = storage.ref();

  return storageRef
    .child(`Worker/${workerId}/photo`)
    .getDownloadURL()
    .then((url) => url)
    .catch(() => null);
};

export const getCompanyImg = (companyId: number): Promise<any> => {
  const storage = firebase.storage();
  const storageRef = storage.ref();

  return storageRef
    .child(`Company/${companyId}/photo`)
    .getDownloadURL()
    .then((url) => {
      return url;
    })
    .catch(() => null);
};

export const updateCompanyImg = (
  companyId: number,
  file: File,
): Promise<firebase.storage.UploadTaskSnapshot | null> => {
  const storage = firebase.storage();
  const storageRef = storage.ref();

  return storageRef
    .child(`Company/${companyId}/photo`)
    .put(file)
    .catch(() => null);
};

export const createCompanyUser = async (email: string, password: string) => {
  return await firebase.auth().createUserWithEmailAndPassword(email, password);
};

/** Narrows an unknown Firebase Storage error down to just the field we branch on. */
const isStorageError = (err: unknown): err is { code: string } =>
  typeof err === "object" && err !== null && "code" in err;

/**
 * Generic JSON settings storage under Company/{companyId}/analytics/{key} —
 * the same Storage "realm" as the company photo (Company/{companyId}/photo),
 * just scoped to an analytics subfolder. Storage only holds blobs, so writes
 * JSON-encode the value and reads fetch the download URL and parse it back.
 *
 * The fetched JSON is untrusted at the type level — it's read as `unknown`
 * and must pass the caller-supplied `isValid` type guard before it's handed
 * back as `T`. Without this, a malformed or unexpectedly-shaped stored
 * object (a botched write, a manual edit, a future format change) would be
 * cast straight to `T` and could crash any render/calculation path that
 * assumes the shape holds (e.g. `LocalDate.parse` on a missing date field).
 * Resolves to `null` on any failure — not found, network error, bad JSON,
 * or a value that fails validation — so callers can fall back to a default.
 */
export const getCompanyAnalyticsSettings = <T>(
  companyId: number,
  key: string,
  isValid: (value: unknown) => value is T,
): Promise<T | null> => {
  const storage = firebase.storage();
  const storageRef = storage.ref();
  const path = `Company/${companyId}/analytics/${key}`;

  return storageRef
    .child(path)
    .getDownloadURL()
    .then((url) =>
      fetch(url)
        .then((res) => res.json() as Promise<unknown>)
        .then((value) => {
          if (isValid(value)) return value;
          console.error(
            `[analytics settings] stored value at "${path}" failed shape validation — ignoring it and falling back to defaults.`,
            value,
          );
          return null;
        })
        .catch((err: unknown) => {
          // Distinct from the getDownloadURL() catch below: this means the
          // download URL resolved fine but the raw fetch() to it was blocked
          // — almost always the Storage bucket having no CORS policy for
          // plain fetch()/XHR reads (the Firebase SDK's own calls are
          // pre-authorized cross-origin; a manual fetch() to the download
          // URL is not, unless the bucket's CORS config allows this origin).
          console.error(
            `[analytics settings] fetch() of the download URL failed for "${path}" — check the browser Network tab for a CORS error, and see the Storage bucket's CORS configuration.`,
            err,
          );
          return null;
        }),
    )
    .catch((err: unknown) => {
      // "object-not-found" is the normal, expected state for a company that
      // hasn't saved anything to this path yet (e.g. no custom sales/costs
      // added) — not a failure, so don't log it as one. Anything else
      // (storage/unauthorized, network errors, ...) is worth surfacing.
      if (!isStorageError(err) || err.code !== "storage/object-not-found") {
        console.error(
          `[analytics settings] getDownloadURL() failed for "${path}" — check Storage Security Rules allow read on Company/{companyId}/analytics/**.`,
          err,
        );
      }
      return null;
    });
};

export const updateCompanyAnalyticsSettings = <T>(
  companyId: number,
  key: string,
  value: T,
): Promise<void> => {
  const storage = firebase.storage();
  const storageRef = storage.ref();
  const blob = new Blob([JSON.stringify(value)], {
    type: "application/json",
  });

  return storageRef
    .child(`Company/${companyId}/analytics/${key}`)
    .put(blob)
    .then(() => undefined);
};

/**
 * Atomically "claims" a legacy localStorage entry for one-time migration:
 * reads it and removes it from localStorage in the same synchronous step
 * (no `await` in between), so a concurrent call for a *different* company
 * — e.g. a fast company switch, started before this call's Firebase write
 * has resolved — can never also read, and re-migrate, the same value.
 * Returns `null` if there's nothing stored under `key`.
 *
 * If the caller's own migration attempt fails after claiming (the Firebase
 * write throws), call the returned `restore()` to put the raw value back
 * so a future load can retry — otherwise a transient failure would lose
 * the data for good.
 */
export const claimLegacyStorageItem = (
  key: string,
): { raw: string; restore: () => void } | null => {
  const raw = localStorage.getItem(key);
  if (raw === null) return null;
  localStorage.removeItem(key);
  return { raw, restore: () => localStorage.setItem(key, raw) };
};

/**
 * Loads one company analytics setting from Firebase, falling back to a
 * single legacy localStorage key — migrated up to Firebase once, via
 * claimLegacyStorageItem, if found. Returns `null` only when there's
 * genuinely nothing anywhere, so callers can apply their own default.
 *
 * Covers the common shape shared by operating hours and custom costs (one
 * legacy key, no merging). Custom sales has two legacy sources to merge
 * (its own prior key, plus the older "Custom Earnings" feature it replaced)
 * and implements that case itself using the same claimLegacyStorageItem
 * primitive, rather than this helper.
 */
export const loadWithLegacyFallback = async <T>(
  companyId: number,
  key: string,
  legacyKey: string,
  isValid: (value: unknown) => value is T,
  parseLegacy: (raw: string) => T | null,
): Promise<T | null> => {
  const remote = await getCompanyAnalyticsSettings<T>(companyId, key, isValid);
  if (remote) return remote;

  const claimed = claimLegacyStorageItem(legacyKey);
  const legacy = claimed ? parseLegacy(claimed.raw) : null;
  if (legacy === null) return null;

  try {
    await updateCompanyAnalyticsSettings(companyId, key, legacy);
  } catch {
    claimed?.restore();
  }
  return legacy;
};

export const getWorkerVideo = (workerId: string): Promise<any> => {
  const storage = firebase.storage();
  const storageRef = storage.ref();

  return storageRef
    .child(`Worker/${workerId}/video`)
    .getDownloadURL()
    .then((url) => url)
    .catch(() => null);
};
